import {useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState} from 'react';

import {OptionInterface, PaginationInterface, TableInterface} from '@veneer/core';

import {useRefreshableTable} from '../../contexts';
import {GlobalTranslationsContext} from '../../i18n';
import {
    PaginatedRequest,
    PaginatedRequestParams,
    parsePagination,
    parseSort,
    serialiseQueryParams,
    serialiseSortBy,
} from '../../util';

import {DataTableProps, PaginationState, SimpleTableProps, TypedTablePreferences} from './types';

export function useTable<F, O extends string, T>(
    fetch: PaginatedRequest<F, O, T>,
    filters: F,
    initialPreferences?: Pick<TypedTablePreferences<O>, 'sortBy' | 'defaultOrder' | 'width'>
): {
    data: T[];
    table: DataTableProps;
} {
    const {veneer: v} = useContext(GlobalTranslationsContext);

    const [{data, loading}, setState] = useState<{data: T[]; loading: boolean}>({data: [], loading: true});
    const [preferences, setPreferences] = useState<TypedTablePreferences<O>>(() => ({
        sortBy: parseSort(new URLSearchParams(location.search), initialPreferences?.sortBy),
        defaultOrder: initialPreferences?.defaultOrder,
        order: initialPreferences?.defaultOrder,
        width: initialPreferences?.width,
    }));

    const onSort: TableInterface['onSort'] = useCallback((event, sort) => {
        setPreferences(preferences => ({...preferences, sortBy: sort}));
    }, []);

    const onColumnReorder: TableInterface['onColumnReorder'] = useCallback(newOrder => {
        setPreferences(preferences => ({...preferences, order: newOrder}));
    }, []);

    const handlePageChange: PaginationInterface['onPageChange'] = useCallback(currentPage => {
        setPagination(pagination => ({...pagination, currentPage}));
    }, []);

    const handlePageSizeChange: PaginationInterface['onPageSizeChange'] = useCallback((e, option: OptionInterface) => {
        setPagination(pagination => ({...pagination, pageSize: option.value as number}));
    }, []);

    const [pagination, setPagination] = useState<PaginationState>(() => ({
        pageSizeOptions: [10, 20, 50, 100].map(value => ({value})),
        totalItems: 0,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        i18n: v.pagination,
        ...parsePagination(new URLSearchParams(location.search)),
    }));

    useEffect(() => {
        setPagination(pagination => ({...pagination, currentPage: 1}));
        // Here be magic. When filters changes, reset the page to one.
    }, [filters]);

    const setTotalItems = useCallback(totalItems => {
        setPagination(pagination => ({...pagination, totalItems}));
    }, []);

    const requestParams: PaginatedRequestParams<F, O> = useMemo(() => {
        return {
            ordering: serialiseSortBy(preferences.sortBy),
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            ...filters,
        };
    }, [preferences.sortBy, filters, pagination.currentPage, pagination.pageSize]);

    const refresh = useCallback(() => {
        setState(state => ({...state, loading: true}));
        void fetch(requestParams).then(data => {
            setTotalItems(data.count);
            setState({data: data.results, loading: false});
        });
    }, [fetch, requestParams, setTotalItems]);

    useEffect(() => refresh(), [refresh]);

    const setLoading = useCallback((loading: boolean) => setState(state => ({...state, loading})), []);

    useEffect(() => {
        history.replaceState(null, document.title, `?${serialiseQueryParams(requestParams).toString()}`);
    }, [requestParams]);

    const tableRef = useRefreshableTable();
    useImperativeHandle(tableRef?.ref, () => ({
        refresh,
        setLoading,
    }));

    return {
        table: {
            preferences,
            pagination,
            loading,
            onSort,
            onColumnReorder,
            loadingDataLength: 10,
            showFilters: true,
            columnReorder: true,
            columnResize: true,
        },
        data,
    };
}

export function useSimpleTable<T>(fetch: () => Promise<T[]>): {
    data: T[];
    refresh: () => void;
    table: SimpleTableProps;
} {
    const [{data, loading}, setState] = useState<{data: T[]; loading: boolean}>({data: [], loading: true});

    const refresh = useCallback(() => {
        setState(state => ({...state, loading: true}));
        void fetch().then(data => {
            setState({data, loading: false});
        });
    }, [fetch]);

    useEffect(() => refresh(), [refresh]);

    return {
        table: {
            loading,
            loadingDataLength: 10,
            columnResize: true,
        },
        data,
        refresh,
    };
}
